/* eslint-disable max-classes-per-file, class-methods-use-this */

export const SECTION_KINDS = ["main", "other", "nearby"] as const
export type SearchSectionKind = typeof SECTION_KINDS[number]

function formatPpl(value: number) {
  if (value > 1000000) return `${Math.round(value / 1000000)}m`
  if (value > 1000) return `${Math.round(value / 1000)}k`
  return String(value)
}

export type SearchResponseItem = {
  type: string
  subtype?: string
  name: string
  name_en?: string
  url_part: string
  country_iso: string
  country_name: string
  region_name?: string
  locations_count?: number
  bbox?: string
  population?: number
  dist?: number
}

export type SearchSection = {
  kind: SearchSectionKind
  title?: string
  items: SearchItem[]
}

export type SearchResult = SearchSection[]

const routingPrefixFor = (subtype?: string) => {
  if (subtype === "bikepark") return "bike-parks"
  if (subtype === "hut" || subtype === "lodge") return "huts-and-lodges"
  if (subtype === "pass") return "passes"
  return "peaks"
}

export abstract class SearchItem {
  readonly name: string
  readonly englishName?: string
  readonly urlPart: string
  readonly countryIso: string
  readonly dist?: number

  constructor(attrs: SearchResponseItem) {
    this.name = attrs.name
    this.englishName = attrs.name_en
    this.urlPart = attrs.url_part
    this.countryIso = attrs.country_iso
    this.dist = attrs.dist
  }

  abstract domain(): string
  abstract path(): string
  abstract itemCss(): string[]
  abstract subtitleIconCss(section: SearchSection): string[] | undefined
  abstract subtitle(section: SearchSection): string | undefined

  iconCss(): string[] {
    return []
  }

  open(beforeRedirect?: () => void) {
    const domain = this.domain()
    const path = this.path()
    if (window.location.host.endsWith(domain)) {
      if (beforeRedirect) beforeRedirect()
      window.location.href = path
    } else {
      // TODO: handle lang subdomains
      window.open(`https://www.${domain}${path}`, "_blank")
    }
  }
}

export abstract class Country extends SearchItem {
  readonly locationsCount?: number

  constructor(attrs: SearchResponseItem) {
    super(attrs)
    this.locationsCount = attrs.locations_count
  }

  abstract domain(): string
  abstract path(): string

  itemCss() {
    return ["country"]
  }

  iconCss() {
    return []
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  subtitleIconCss(_section: SearchSection) {
    return this.countryIso ? ["flag", `flag-${this.countryIso.toLowerCase()}`] : undefined
  }
}

export class SnowCountry extends Country {
  domain() {
    return "snow-forecast.com"
  }

  path() {
    return `/countries/${this.urlPart}/resorts`
  }

  itemCss() {
    return ["country", "snow"]
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  subtitle(_section: SearchSection) {
    const count = this.locationsCount
    return `${count} ${count === 1 ? "resort" : "resorts"}`
  }
}

export class MountainCountry extends Country {
  domain() {
    return "mountain-forecast.com"
  }

  path() {
    return `/countries/${this.urlPart}`
  }

  itemCss() {
    return ["country", "mountain"]
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  subtitle(_section: SearchSection) {
    const count = this.locationsCount
    return `${count} ${count === 1 ? "peak" : "peaks"}`
  }
}

export class TideCountry extends Country {
  domain() {
    return "tide-forecast.com"
  }

  path() {
    return `/countries/${this.urlPart}`
  }

  itemCss() {
    return ["country", "tide"]
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  subtitle(_section: SearchSection) {
    const count = this.locationsCount
    return `${count} ${count === 1 ? "location" : "locations"}`
  }
}

export class WeatherCountry extends Country {
  domain() {
    return "weather-forecast.com"
  }

  path() {
    return `/countries/${this.urlPart}`
  }

  itemCss() {
    return ["country", "weather"]
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  subtitle(_section: SearchSection) {
    const count = this.locationsCount
    return `${count} ${count === 1 ? "city" : "cities"}`
  }
}

export class SurfCountry extends Country {
  domain() {
    return "surf-forecast.com"
  }

  path() {
    return `/countries/${this.urlPart}/breaks`
  }

  itemCss() {
    return ["country", "surf"]
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  subtitle(_section: SearchSection) {
    const count = this.locationsCount
    return `${count} ${count === 1 ? "break" : "breaks"}`
  }
}

export abstract class City extends SearchItem {
  readonly locationsCount?: number
  readonly bbox?: string
  readonly population?: number

  constructor(attrs: SearchResponseItem) {
    super(attrs)
    this.locationsCount = attrs.locations_count
    this.bbox = attrs.bbox
    this.population = attrs.population
  }

  abstract domain(): string

  path() {
    return `/countries/${this.urlPart}?bbox=${this.bbox}`
  }

  locationName() {
    return "location"
  }

  itemCss() {
    return ["city"]
  }

  iconCss() {
    return []
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  subtitleIconCss(_section: SearchSection) {
    return this.countryIso ? ["flag", `flag-${this.countryIso.toLowerCase()}`] : undefined
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  subtitle(_section: SearchSection) {
    const population = this.population ? `${formatPpl(this.population)} people, ` : ""

    const count = this.locationsCount
    let locationName = this.locationName()
    if (count !== 1) locationName += "s"
    const locations = `${count} ${locationName} nearby`

    return `${population}${locations}`
  }
}

export class SnowCity extends City {
  constructor(attrs: SearchResponseItem) {
    super({
      ...attrs,
      name: `${attrs.name}, ${attrs.country_name}`,
    })
  }

  domain() {
    return "snow-forecast.com"
  }

  path() {
    return `/cities/${this.urlPart}`
  }

  locationName() {
    return "resort"
  }

  itemCss() {
    return ["city", "snow"]
  }
}

export class MountainCity extends City {
  constructor(attrs: SearchResponseItem) {
    super({
      ...attrs,
      name: `${attrs.name}, ${attrs.country_name}`,
    })
  }

  domain() {
    return "mountain-forecast.com"
  }

  path() {
    return `/cities/${this.urlPart}`
  }

  locationName() {
    return "peak"
  }

  itemCss() {
    return ["city", "mountain"]
  }
}

export class TideCity extends City {
  constructor(attrs: SearchResponseItem) {
    super({
      ...attrs,
      name: `${attrs.name}, ${attrs.country_name}`,
    })
  }

  domain() {
    return "tide-forecast.com"
  }

  path() {
    return `/cities/${this.urlPart}`
  }

  locationName() {
    return "location"
  }

  itemCss() {
    return ["city", "tide"]
  }
}

export class SurfCity extends City {
  constructor(attrs: SearchResponseItem) {
    super({
      ...attrs,
      name: `${attrs.name}, ${attrs.country_name}`,
    })
  }

  domain() {
    return "surf-forecast.com"
  }

  path() {
    return `/cities/${this.urlPart}`
  }

  locationName() {
    return "break"
  }

  itemCss() {
    return ["city", "surf"]
  }
}

export class SurfRegion extends SurfCity {
  path() {
    return `/regions/${this.urlPart}`
  }
}

export class SurfProvince extends SurfCity {
  path() {
    return `/provinces/${this.urlPart}/breaks`
  }
}

export abstract class Location extends SearchItem {
  readonly countryName: string
  readonly subtype?: string

  constructor(attrs: SearchResponseItem) {
    super(attrs)
    this.countryName = attrs.country_name
    this.subtype = attrs.subtype
  }

  abstract kind(): string
  abstract domain(): string
  abstract path(): string
  abstract itemCss(): string[]

  subtitleIconCss(section: SearchSection) {
    if (section.kind !== "main" || !this.countryIso) return undefined

    return ["flag", `flag-${this.countryIso.toLowerCase()}`]
  }

  subtitle(section: SearchSection) {
    if (section.kind === "main") return this.countryName
    if (section.kind === "other") return `${this.kind()} - ${this.countryName}`
    return this.kind()
  }
}

export class SnowLocation extends Location {
  kind() {
    return "Ski Resort"
  }

  itemCss() {
    return ["snow"]
  }

  domain() {
    return "snow-forecast.com"
  }

  path() {
    return `/resorts/${this.urlPart}/6day/mid`
  }
}

export class MountainLocation extends Location {
  kind() {
    return "Mountain"
  }

  domain() {
    return "mountain-forecast.com"
  }

  path() {
    const prefix = routingPrefixFor(this.subtype)
    return `/${prefix}/${this.urlPart}/forecasts/latest`
  }

  itemCss() {
    const css = ["mountain"]
    if (this.subtype) css.push(`mountain-${this.subtype}`)
    return css
  }
}

export class SurfLocation extends Location {
  kind() {
    return "Surf Break"
  }

  itemCss() {
    return ["surf"]
  }

  domain() {
    return "surf-forecast.com"
  }

  path() {
    return `/breaks/${this.urlPart}/forecasts/latest/six_day`
  }
}

export class TideLocation extends Location {
  kind() {
    return "Tide Location"
  }

  itemCss() {
    return ["tide"]
  }

  domain() {
    return "tide-forecast.com"
  }

  path() {
    return `/tide/${this.urlPart}/tide-times`
  }
}

export class WeatherLocation extends Location {
  readonly regionName?: string

  constructor(attrs: SearchResponseItem) {
    super(attrs)
    this.regionName = attrs.region_name
  }

  kind() {
    return "Town/City"
  }

  itemCss() {
    return ["weather"]
  }

  domain() {
    return "weather-forecast.com"
  }

  path() {
    return `/locations/${this.urlPart}/forecasts/latest`
  }

  subtitle(section: SearchSection) {
    const place = this.regionName ?? this.countryName
    if (section.kind === "main") return place
    if (section.kind === "other") return `${this.kind()} - ${place}`
    return this.kind()
  }
}
